import { useCallback, useEffect, useMemo, useState, ReactNode } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  Box,
  Button,
  ChoicePanel,
  Dialog,
  Divider,
  IconButton,
  Icons,
  Inset,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';
import { trackClusterConnected } from '@cast/infrastructure-analytics';
import { useNavigate } from '@cast/infrastructure-organization';
import { Cloud } from '@cast/shared-types';
import { useReportingTrialFleetStats } from '@cast/features-cluster-shared';
import {
  EXTERNAL_CLUSTERS_QUERY_KEY,
  INVENTORY_ASSET_LIST_CLUSTERS_QUERY_KEY,
} from '@cast/shared-cluster-context-data-access';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import {
  FREE_VCPU_CAP,
  ReportingTrialConnectionAlert,
  useReportingTrial,
} from '@cast/shared-trial-ui';

import { CLOUD_ASSET_INTEGRATIONS_QUERY_KEY } from '@oneclick/hooks/queries/inventory/useCloudAssetIntegrationsQuery';
import { CloudConnect } from '@oneclick/pages/onboarding/steps/Step2/right-side/CloudConnect';
import { Single } from '@oneclick/pages/onboarding/steps/Step2/right-side/Single';
import { Snapshot } from '@oneclick/pages/onboarding/steps/Step2/right-side/Snapshot';
import {
  CardShell,
  ONBOARDING_MODAL_HEIGHT,
  ONBOARDING_MODAL_MAX_WIDTH,
} from '@oneclick/pages/onboarding/_components/CardShell';
import { ConnectionChoiceIcon } from '@oneclick/pages/onboarding/_components/ConnectionChoiceIcon';
import { ConnectingChecklist } from '@oneclick/pages/onboarding/_components/connecting-result/ConnectingChecklist';
import { DiscoveredStatsGrid } from '@oneclick/pages/onboarding/_components/connecting-result/DiscoveredStatsGrid';
import { useOnboardingConnectionProgress } from '@oneclick/pages/onboarding/_components/connecting-result/_hooks/useOnboardingConnectionProgress';
import {
  ConnectionChoice,
  CTA_LABELS,
  PROVIDER_LABELS,
  ScriptState,
} from '@oneclick/pages/onboarding/_constants';
import { SETTINGS_PATHS } from '@oneclick/constants/navigation';
import { useReportingContactSalesDialog } from 'core/integrations';

type ChoicePanelProps = {
  title: ConnectionChoice;
  description: string;
  icon: ReactNode;
  testId: string;
  selected?: boolean;
  onSelect?: () => void;
};

const CHOICE_OPTIONS: Array<
  Pick<ChoicePanelProps, 'title' | 'description' | 'icon' | 'testId'>
> = [
  {
    title: ConnectionChoice.cloudConnect,
    description:
      'Grant a read-only IAM role in your cloud account. Always-fresh data to discover optimization potential for your whole infrastructure.',
    icon: <Icons.CloudArrowDown weight="regular" />,
    testId: 'connection-choice-cloud-connect',
  },
  {
    title: ConnectionChoice.snapshot,
    description:
      'Run a script using your CLI and read access. Cost, performance and risk for your clusters — point-in-time only.',
    icon: <Icons.Camera weight="regular" />,
    testId: 'connection-choice-snapshot',
  },
  {
    title: ConnectionChoice.single,
    description: 'Install castctl. For evals and air-gapped environments.',
    icon: <Icons.Terminal weight="regular" />,
    testId: 'connection-choice-single',
  },
];

const Choice = ({
  title,
  description,
  icon,
  testId,
  selected,
  onSelect,
}: ChoicePanelProps) => (
  <ChoicePanel selected={selected} onClick={onSelect} testId={testId}>
    <Inset
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 16,
        p: 16,
      }}
    >
      <ConnectionChoiceIcon>{icon}</ConnectionChoiceIcon>
      <Stack gap={4} flex={1}>
        <Typography variant="h6" color={tokens.theme.fg.structural.primary}>
          {title}
        </Typography>
        <Typography variant="P14R" color={tokens.theme.fg.structural.secondary}>
          {description}
        </Typography>
      </Stack>
    </Inset>
  </ChoicePanel>
);

type Phase = 'setup' | 'connecting' | 'ready' | 'error';

const INITIAL_SCRIPT_STATE: ScriptState = {
  isLoading: true,
  hasError: false,
};

type ConnectionFlowLayout = 'card' | 'dialog';

export type ConnectionFlowProps = {
  initialChoice?: ConnectionChoice;
  layout?: ConnectionFlowLayout;
  onBack?: () => void;
  onClose?: () => void;
  onSuccess?: () => void;
  showSkipForNow?: boolean;
  cardCurrentStep?: number;
  cardTotalSteps?: number;
};

export const ConnectionFlow = ({
  initialChoice = ConnectionChoice.cloudConnect,
  layout = 'card',
  onBack,
  onClose,
  onSuccess,
  showSkipForNow = true,
  cardCurrentStep,
  cardTotalSteps = 3,
}: ConnectionFlowProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganizations();
  const { isPreTrial, isStartingTrial, startTrial } = useReportingTrial();
  const fleet = useReportingTrialFleetStats();
  const { openReportingContactSales } = useReportingContactSalesDialog();
  const [phase, setPhase] = useState<Phase>('setup');
  const [selectedChoice, setSelectedChoice] =
    useState<ConnectionChoice>(initialChoice);
  const [scriptState, setScriptState] =
    useState<ScriptState>(INITIAL_SCRIPT_STATE);
  const [cloudProvider, setCloudProvider] = useState<Cloud>(Cloud.aws);
  const [connectionStartedAt, setConnectionStartedAt] = useState<
    Date | undefined
  >();

  const handleScriptStateChange = useCallback((state: ScriptState) => {
    setScriptState(state);
  }, []);

  const handleChoiceSelect = useCallback(
    (choice: ConnectionChoice) => {
      if (choice === selectedChoice) {
        return;
      }
      setSelectedChoice(choice);
      setScriptState(INITIAL_SCRIPT_STATE);
    },
    [selectedChoice]
  );

  const handleContinue = useCallback(() => {
    setConnectionStartedAt(new Date());
    setPhase('connecting');
  }, []);

  const showConnectionResult =
    phase === 'connecting' || phase === 'ready' || phase === 'error';

  const connectionProgress = useOnboardingConnectionProgress({
    connectionChoice: selectedChoice,
    isConnecting: phase === 'connecting',
    showResult: showConnectionResult,
    connectionStartedAt,
    captureBaseline: phase === 'setup',
  });
  const newClusterId = connectionProgress.newConnections[0]?.id;

  const tryStartTrialIfOverFreeCap = useCallback(() => {
    if (!isPreTrial || isStartingTrial) {
      return;
    }
    // Fail closed: only auto-start when fleet avg is known and above Free.
    if (fleet.isLoading || fleet.hasOverviewError) {
      return;
    }
    if (fleet.avgProvisionedVcpu <= FREE_VCPU_CAP) {
      return;
    }
    startTrial();
  }, [
    fleet.avgProvisionedVcpu,
    fleet.hasOverviewError,
    fleet.isLoading,
    isPreTrial,
    isStartingTrial,
    startTrial,
  ]);

  useEffect(() => {
    if (phase !== 'connecting') {
      return;
    }

    if (connectionProgress.isComplete) {
      if (newClusterId && currentOrganization?.id) {
        trackClusterConnected({
          organization_id: currentOrganization.id,
          cluster_id: newClusterId,
        });
      }

      // Start the reporting trial at the completion-detection point so it
      // fires before any unmount-driven teardown can skip the phase='ready'
      // effect. Deferred below if fleet stats are still loading.
      tryStartTrialIfOverFreeCap();

      setPhase('ready');
      return;
    }

    if (connectionProgress.isTimedOut) {
      setPhase('error');
    }
  }, [
    connectionProgress.isComplete,
    connectionProgress.isTimedOut,
    currentOrganization?.id,
    newClusterId,
    phase,
    tryStartTrialIfOverFreeCap,
  ]);

  // If connect completed before fleet overview resolved, start trial once
  // avg provisioned vCPU is known and exceeds the Free cap.
  useEffect(() => {
    if (phase !== 'ready') {
      return;
    }
    tryStartTrialIfOverFreeCap();
  }, [phase, tryStartTrialIfOverFreeCap]);

  const handleFinish = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [EXTERNAL_CLUSTERS_QUERY_KEY],
      }),
      queryClient.invalidateQueries({
        queryKey: [INVENTORY_ASSET_LIST_CLUSTERS_QUERY_KEY],
      }),
      queryClient.invalidateQueries({
        queryKey: [CLOUD_ASSET_INTEGRATIONS_QUERY_KEY],
      }),
    ]);
    connectionProgress.refetchClusters?.();

    if (onSuccess) {
      onSuccess();
      return;
    }

    navigate('/clusters');
  }, [connectionProgress, navigate, onSuccess, queryClient]);

  const handleSkip = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }

    navigate('/clusters');
  }, [navigate, onClose]);

  const handleBack = useCallback(() => {
    if (phase === 'setup') {
      onBack?.();
      return;
    }

    setPhase('setup');
    setConnectionStartedAt(undefined);
  }, [onBack, phase]);

  const handleRetry = useCallback(() => {
    setConnectionStartedAt(new Date());
    setPhase('connecting');
  }, []);

  const isScriptActionDisabled = scriptState.isLoading || scriptState.hasError;

  const cardTitle = useMemo(() => {
    if (phase === 'ready') {
      return 'Connection ready';
    }

    if (phase === 'error') {
      return 'Connection timed out';
    }

    if (phase === 'connecting') {
      if (selectedChoice === ConnectionChoice.single) {
        return 'Connecting your cluster';
      }

      const provider =
        selectedChoice === ConnectionChoice.snapshot
          ? Cloud.aws
          : cloudProvider;

      return `Connecting · ${PROVIDER_LABELS[provider] ?? provider}`;
    }

    return 'How would you like to connect?';
  }, [cloudProvider, phase, selectedChoice]);

  const currentStep =
    cardCurrentStep ??
    (phase === 'setup' ? cardTotalSteps - 1 : cardTotalSteps);

  const selectedChoiceDescription = useMemo(
    () =>
      CHOICE_OPTIONS.find((option) => option.title === selectedChoice)
        ?.description ?? '',
    [selectedChoice]
  );

  const setupActions = (
    <Stack direction="row" gap={10}>
      <Button
        variant="primary"
        disabled={isScriptActionDisabled}
        onClick={handleContinue}
        testId="connect-cluster-continue-button"
      >
        {CTA_LABELS[selectedChoice]}
      </Button>
      {showSkipForNow ? (
        <Button variant="ghost" onClick={handleSkip}>
          Skip for now
        </Button>
      ) : null}
    </Stack>
  );

  // Block finish until connection completes (and trial start finishes) so
  // early unmount cannot skip reporting trial activation.
  const isFinishBlocked =
    phase === 'connecting' || (isPreTrial && isStartingTrial);

  const resultActions =
    phase === 'ready' || phase === 'connecting' ? (
      <Button
        variant="primary"
        onClick={handleFinish}
        disabled={isFinishBlocked}
        testId="go-to-console-button"
      >
        Go to console
      </Button>
    ) : phase === 'error' ? (
      <Stack direction="row" gap={10}>
        <Button variant="primary" onClick={handleRetry}>
          Try again
        </Button>
        <Button variant="ghost" onClick={handleBack}>
          Back
        </Button>
      </Stack>
    ) : undefined;

  const showBack = phase !== 'setup' || !!onBack;

  const trialConnectionAlert = (
    <ReportingTrialConnectionAlert
      sx={{ width: '100%', mt: 16 }}
      fleet={fleet}
      onViewPlans={() => navigate(SETTINGS_PATHS.platformUsageReportingPlans)}
      onContactSales={() => openReportingContactSales()}
    />
  );

  const flowContent =
    phase === 'setup'
      ? {
          left: (
            <Stack gap={tokens.primitives.space[400]} width={604} pr={20}>
              {CHOICE_OPTIONS.map((option) => (
                <Choice
                  key={option.title}
                  {...option}
                  selected={option.title === selectedChoice}
                  onSelect={() => handleChoiceSelect(option.title)}
                />
              ))}
            </Stack>
          ),
          right: (
            <Stack gap={0} width="100%">
              {selectedChoice === ConnectionChoice.cloudConnect ? (
                <CloudConnect
                  onScriptStateChange={handleScriptStateChange}
                  onProviderChange={setCloudProvider}
                />
              ) : selectedChoice === ConnectionChoice.snapshot ? (
                <Snapshot onScriptStateChange={handleScriptStateChange} />
              ) : (
                <Single
                  isDialogLayout={layout === 'dialog'}
                  onScriptStateChange={handleScriptStateChange}
                />
              )}
              {trialConnectionAlert}
            </Stack>
          ),
        }
      : {
          left:
            phase === 'error' ? (
              <Stack gap={16} width={604} pr={20}>
                <Typography
                  variant="P14R"
                  color={tokens.theme.fg.structural.secondary}
                >
                  We couldn&apos;t detect a connection within 5 minutes. Make
                  sure{' '}
                  {selectedChoice === ConnectionChoice.single
                    ? 'castctl'
                    : 'the script'}{' '}
                  completed successfully, then try again.
                </Typography>
              </Stack>
            ) : (
              <ConnectingChecklist
                phase={phase === 'ready' ? 'ready' : 'connecting'}
                leadText={selectedChoiceDescription}
                activeChecklistStep={connectionProgress.activeChecklistStep}
              />
            ),
          right: (
            <DiscoveredStatsGrid
              stats={connectionProgress.stats}
              isLoading={connectionProgress.isSummaryLoading}
              ready={phase === 'ready'}
            />
          ),
        };

  const modalHeaderEndContent = onClose ? (
    <Stack direction="row" gap={8} alignItems="center">
      {phase !== 'setup' ? (
        <>
          <Button
            variant="ghost"
            size="sm"
            startIcon={<Icons.ArrowLeft size={16} />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Divider orientation="vertical" sx={{ height: 20 }} />
        </>
      ) : null}
      <IconButton
        variant="ghost"
        size="sm"
        onClick={onClose}
        aria-label="Close"
        testId="connect-cluster-close-button"
      >
        <Icons.X />
      </IconButton>
    </Stack>
  ) : null;

  const shell = (
    <CardShell
      title={cardTitle}
      titleVariant={layout === 'card' ? 'h5' : 'A1'}
      fillHeight={layout === 'dialog'}
      currentStep={layout === 'card' ? currentStep : undefined}
      totalSteps={layout === 'card' ? cardTotalSteps : undefined}
      headerEndContent={layout === 'dialog' ? modalHeaderEndContent : undefined}
      onBack={layout === 'card' && showBack ? handleBack : undefined}
      actions={phase === 'setup' ? setupActions : resultActions}
      slots={flowContent}
    />
  );

  if (layout === 'dialog') {
    return (
      <Dialog
        open
        onClose={onClose}
        testId="connect-cluster-dialog"
        disableCloseOnBackdropClick
        size={{
          width: ONBOARDING_MODAL_MAX_WIDTH,
          maxWidth: `min(${ONBOARDING_MODAL_MAX_WIDTH}px, 96vw)`,
        }}
        PaperProps={{
          sx: {
            height: `min(${ONBOARDING_MODAL_HEIGHT}px, calc(100dvh - 48px))`,
            maxHeight: 'calc(100dvh - 48px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {shell}
        </Box>
      </Dialog>
    );
  }

  return shell;
};
